
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { Car, useCar } from '@/contexts/CarContext';
import { useAuth } from '@/contexts/AuthProvider';
import { CarIcon, Plus, BarChart2, BookmarkCheck, CircleDot, Circle, Edit, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import PageLayout from '@/components/PageLayout';
import StatusWidget from '@/components/StatusWidget';
import IssueTimeline from '@/components/IssueTimeline';
import ServiceRecords from '@/components/ServiceRecords';
import CostsWidget from '@/components/CostsWidget';
import NewActivityCard from '@/components/NewActivityCard';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { getSignedViewUrl, type SignedUrlInfo } from '@/lib/getSignedViewUrl';
import { useToast } from '@/components/ui/use-toast';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import NavBar from '@/components/NavBar';
import { useI18n } from '@/i18n/LanguageContext';
import { supabase } from '@/lib/supabase';
import AIAssistantComingSoonModal from '@/components/AIAssistantComingSoonModal';
import { fetchCarActivitySummary, type CarActivityCounts } from '@/lib/activity/carActivity';

const HomePage = () => {
  // All hooks at the top
  const {
    user
  } = useAuth();
  const {
    cars
  } = useCar();
  const {
    currentCarId,
    currentCar,
    selectedCarId,
    mainCarId,
    selectCar,
    resetToMainCar
  } = useCarSelectionContext();
  const {
    t: tSettings
  } = useSettings();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('issues');
  const [signedUrls, setSignedUrls] = useState<Record<string, SignedUrlInfo>>({});
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentCarIndex, setCurrentCarIndex] = useState(0);
  const { toast } = useToast();
  const [tasksCount, setTasksCount] = useState<number>(0);
  const [serviceEntriesCount, setServiceEntriesCount] = useState<number>(0);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [activeServiceRequests, setActiveServiceRequests] = useState<Array<{
    id: string;
    title?: string;
    created_at: string;
    owner_notes: string;
  }>>([]);
  const [activityCounts, setActivityCounts] = useState<CarActivityCounts | null>(null);
  const [aiAssistantModalOpen, setAIAssistantModalOpen] = useState(false);
  const loadedUserIdRef = useRef<string | null>(null); // Track which user ID we've loaded for
  
  // Add ref to track which cars have already been fetched to prevent infinite loops
  const fetchedCarIds = useRef<Set<string>>(new Set());
  // Add ref to track which cars have had their loading state initialized
  const initializedLoadingStates = useRef<Set<string>>(new Set());

  // Main car first for carousel (used in effect below and in render)
  const carsForCarousel = React.useMemo(() => {
    if (!mainCarId || cars.length <= 1) return cars;
    const main = cars.find(c => c.id === mainCarId);
    const rest = cars.filter(c => c.id !== mainCarId);
    return main ? [main, ...rest] : cars;
  }, [cars, mainCarId]);

  // Initialize with main car selected (use carsForCarousel so index matches carousel order)
  useEffect(() => {
    if (carsForCarousel.length > 0 && currentCarId) {
      const carIndex = carsForCarousel.findIndex(car => car.id === currentCarId);
      if (carIndex !== -1) {
        setCurrentCarIndex(carIndex);
        if (carouselApi) {
          carouselApi.scrollTo(carIndex);
        }
      }
    }
  }, [carsForCarousel, currentCarId, carouselApi]);

  // Add effect for slide changes
  useEffect(() => {
    if (!carouselApi) return;

    // Add event listener for slide changes
    const onSelect = () => {
      const newIndex = carouselApi.selectedScrollSnap();
      setCurrentCarIndex(newIndex);
      // Don't automatically select car when swiping - only update index
    };
    carouselApi.on("select", onSelect);

    // Cleanup
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  // Handle car selection - only when explicitly clicked (use carsForCarousel for index)
  const handleCarSelect = (carId: string) => {
    console.log('🚗 Selecting car:', carId);
    selectCar(carId);
    const carIndex = carsForCarousel.findIndex(car => car.id === carId);
    if (carIndex !== -1 && carouselApi) {
      carouselApi.scrollTo(carIndex);
    }
  };

  // Handle image loading states
  const handleImageLoad = (carId: string) => {
    console.log(`✅ Image loaded for car ${carId}`);
    setImageLoadingStates(prev => ({
        ...prev,
      [carId]: false
    }));
  };

  const handleImageError = (carId: string) => {
    console.log(`❌ Image failed to load for car ${carId}`);
    setImageLoadingStates(prev => ({
      ...prev,
      [carId]: false
    }));
  };

  // Enhanced caching for signed URLs with localStorage persistence
  useEffect(() => {
    // Load cached URLs from localStorage on mount
    const loadCachedUrls = () => {
      try {
        const cached = localStorage.getItem('car-signed-urls');
        if (cached) {
          const parsed = JSON.parse(cached);
          const now = Date.now();
          const validUrls = Object.fromEntries(
            Object.entries(parsed).filter(([_, urlInfo]: [string, any]) => 
              urlInfo.expiresAt && urlInfo.expiresAt > now
            )
          ) as Record<string, SignedUrlInfo>;
          
          // Clean up expired entries from localStorage
          Object.keys(parsed).forEach(key => {
            if (!validUrls[key]) delete parsed[key];
          });
          localStorage.setItem('car-signed-urls', JSON.stringify(parsed));
          
          if (Object.keys(validUrls).length > 0) {
            console.log('📦 Loaded cached signed URLs:', Object.keys(validUrls).length);
            setSignedUrls(validUrls);
            // Mark these cars as fetched
            Object.keys(validUrls).forEach(carId => fetchedCarIds.current.add(carId));
          }
        }
      } catch (e) {
        console.warn('Failed to load cached signed URLs:', e);
      }
    };

    // Save URLs to localStorage
    const saveCachedUrls = (urls: Record<string, SignedUrlInfo>) => {
      try {
        localStorage.setItem('car-signed-urls', JSON.stringify(urls));
      } catch (e) {
        console.warn('Failed to save cached signed URLs:', e);
      }
    };

    // Load cached URLs on first mount
    if (Object.keys(signedUrls).length === 0) {
      loadCachedUrls();
    }

    // Reset fetched car IDs if cars array changes significantly
    const currentCarIds = new Set(cars.map(c => c.id));
    const previouslyFetchedIds = fetchedCarIds.current;
    const previouslyInitializedIds = initializedLoadingStates.current;
    
    // If we have new cars that weren't fetched before, clear the cache
    const hasNewCars = cars.some(car => !previouslyFetchedIds.has(car.id));
    if (hasNewCars) {
      console.log('🔄 New cars detected, clearing fetch cache');
      fetchedCarIds.current.clear();
      initializedLoadingStates.current.clear();
    }

    async function fetchSignedUrls() {
      console.log('🔄 Fetching signed URLs for cars:', cars.length);
      const urlMap: Record<string, SignedUrlInfo> = {};

      for (const car of cars) {
        if (car.image_path) {
          // Check if we already have a valid signed URL
          const existingUrl = signedUrls[car.id];
          const isExpired = existingUrl && existingUrl.expiresAt && existingUrl.expiresAt < Date.now();
          
          if (!fetchedCarIds.current.has(car.id) || isExpired) {
            console.log(`📸 Fetching signed URL for car ${car.id} with path: ${car.image_path}${isExpired ? ' (expired)' : ''}`);
            try {
              const signedUrlInfo = await getSignedViewUrl(car.image_path);
              urlMap[car.id] = {
                ...signedUrlInfo,
                expiresAt: Date.now() + 10 * 60 * 1000 // 10 minutes TTL - matches Edge Function expiry
              };
              fetchedCarIds.current.add(car.id);
              console.log(`✅ Got signed URL for car ${car.id}:`, signedUrlInfo.url);
            } catch (e) {
              console.error(`❌ Failed to get signed URL for car ${car.id}:`, e);
              // Don't set fallback here, let the img onError handle it
            }
          } else {
            console.log(`📸 Car ${car.id} already fetched and not expired, skipping`);
          }
        } else {
          console.log(`📸 Car ${car.id} has no image_path`);
        }
      }

      // Merge with existing signed URLs instead of replacing
      if (Object.keys(urlMap).length > 0) {
        console.log("🔄 Setting signed URLs:", urlMap);
        setSignedUrls(prev => {
          // Clean up expired URLs while merging
          const cleaned = Object.fromEntries(
            Object.entries(prev).filter(([_, urlInfo]) => 
              !urlInfo.expiresAt || urlInfo.expiresAt > Date.now()
            )
          );
          const merged = { ...cleaned, ...urlMap };
          console.log("🔄 Merged signed URLs:", merged);
          console.log("🔄 URLs for each car:", Object.keys(merged).map(carId => ({
            carId,
            hasUrl: !!merged[carId]?.url,
            url: merged[carId]?.url?.substring(0, 50) + '...'
          })));
          
          // Save to localStorage
          saveCachedUrls(merged);
          
          return merged;
        });
      }
    }
    if (cars.length > 0) fetchSignedUrls();

    // Cleanup function to prevent memory leaks
    return () => {
      // Only clear if component is unmounting (not just re-rendering)
      if (cars.length === 0) {
        fetchedCarIds.current.clear();
      }
    };
  }, [JSON.stringify(cars.map(c => `${c.id}:${c.image_path}`))]);

  // Debug: Log signedUrls changes
  useEffect(() => {
    console.log("🔄 signedUrls state changed:", signedUrls);
  }, [signedUrls]);

  // Debug: Log cars and their issues
  useEffect(() => {
    console.log("🚗 Cars state:", cars.map(car => ({
      id: car.id,
      nickname: car.nickname,
      brand: car.brand,
      model: car.model,
      issuesCount: car.issues?.length || 0,
      openIssues: car.issues?.filter(issue => 
        issue.status === 'open' || issue.status === 'recurred'
      ) || []
    })));
  }, [cars]);

  // Auto-refresh signed URLs every 8 minutes to prevent expiration
  useEffect(() => {
    if (cars.length === 0) return;
    
    const interval = setInterval(async () => {
      console.log('🔄 Auto-refreshing signed URLs...');
      const urlMap: Record<string, SignedUrlInfo> = {};
      
      for (const car of cars) {
        if (car.image_path) {
          try {
            const signedUrlInfo = await getSignedViewUrl(car.image_path);
            urlMap[car.id] = {
              ...signedUrlInfo,
              expiresAt: Date.now() + 10 * 60 * 1000 // 10 minutes TTL - matches Edge Function expiry
            };
            console.log(`✅ Refreshed signed URL for car ${car.id}`);
          } catch (e) {
            console.error(`❌ Failed to refresh signed URL for car ${car.id}:`, e);
          }
        }
      }
      
      if (Object.keys(urlMap).length > 0) {
        setSignedUrls(prev => {
          const merged = { ...prev, ...urlMap };
          // Save to localStorage
          try {
            localStorage.setItem('car-signed-urls', JSON.stringify(merged));
          } catch (e) {
            console.warn('Failed to save refreshed signed URLs:', e);
          }
          return merged;
        });
      }
    }, 8 * 60 * 1000); // 8 minutes - refresh before 10-minute expiry

    return () => clearInterval(interval);
  }, [cars]);

  // Set loading state when signed URL is fetched
  useEffect(() => {
    const newLoadingStates: Record<string, boolean> = {};
    cars.forEach(car => {
      if (
        car.image_path &&
        signedUrls[car.id]?.url &&
        !initializedLoadingStates.current.has(car.id)
      ) {
        // Only set loading to true for new URLs, not existing ones
        newLoadingStates[car.id] = true;
        initializedLoadingStates.current.add(car.id);
        console.log(`🔄 Setting loading state for car ${car.id}: true (new URL)`);
      }
    });
    
    if (Object.keys(newLoadingStates).length > 0) {
      setImageLoadingStates(prev => {
        const updated = { ...prev, ...newLoadingStates };
        console.log('🔄 Updated loading states:', updated);
        return updated;
      });
    }
  }, [signedUrls, cars]);

  // Fail-safe timeout to prevent perpetual spinners
  useEffect(() => {
    const TIMEOUT_MS = 5000;
    const timeouts: NodeJS.Timeout[] = [];

    cars.forEach((car) => {
      if (
        car.image_path &&
        signedUrls[car.id]?.url &&
        imageLoadingStates[car.id] === true
      ) {
        const timeout = setTimeout(() => {
          console.warn(`⏰ Timeout hit for car ${car.id}, forcing image loading state to false`);
          setImageLoadingStates((prev) => ({
            ...prev,
            [car.id]: false,
          }));
        }, TIMEOUT_MS);

        timeouts.push(timeout);
      }
    });

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [signedUrls, imageLoadingStates, cars]);

  const isEmpty = cars.length === 0;

  // These are always defined, but only used if !isEmpty
  // Use useMemo to ensure activeCar updates when currentCar changes
  const activeCar = React.useMemo(() => {
    return currentCar || cars[0] || null;
  }, [currentCar, cars]);

  // Load “New activity” counts for active car (changes by other users since last_seen_at)
  useEffect(() => {
    if (!activeCar?.id || !user?.id) {
      setActivityCounts(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const userIdShort = user.id.substring(0, 8);
        const carIdShort = activeCar.id.substring(0, 8);
        console.log(
          `🏠 [HomePage] User ${userIdShort} loading activity summary for car ${carIdShort}...`
        );
        const summary = await fetchCarActivitySummary(activeCar.id, user.id);
        if (cancelled) return;
        console.log(
          `🏠 [HomePage] User ${userIdShort} activity summary loaded:`,
          summary.counts
        );
        setActivityCounts(summary.counts);
      } catch (e) {
        const userIdShort = user.id.substring(0, 8);
        console.error(`❌ [HomePage] User ${userIdShort} failed to load activity summary:`, e);
        if (!cancelled) setActivityCounts(null);
      }
    };

    load();

    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [activeCar?.id, user?.id]);

  // Load tasks count for active car
  useEffect(() => {
    if (!activeCar?.id || !user?.id) {
      setTasksCount(0);
      return;
    }

    const loadTasksCount = async () => {
      try {
        // Load tasks from service_entries.suggested_next_fixes
        const { data: serviceEntriesData, error: serviceEntriesError } = await supabase
          .from('service_entries')
          .select('id, suggested_next_fixes')
          .eq('car_id', activeCar.id)
          .not('suggested_next_fixes', 'is', null);

        // Load standalone tasks from tasks table (only pending/scheduled, not completed)
        const { data: standaloneTasksData, error: standaloneTasksError } = await supabase
          .from('tasks')
          .select('id')
          .eq('car_id', activeCar.id)
          .in('status', ['pending', 'scheduled']);

        // Load hidden service tasks to filter them out and respect done status
        const { data: hiddenServiceTasksData, error: hiddenServiceTasksError } = await supabase
          .from('hidden_service_tasks')
          .select('service_entry_id, task_text, hidden, status')
          .eq('user_id', user.id)
          .eq('car_id', activeCar.id);

        if (serviceEntriesError) {
          console.error('Failed to load tasks count from service entries:', serviceEntriesError);
        }
        if (standaloneTasksError) {
          console.error('Failed to load tasks count from tasks table:', standaloneTasksError);
        }
        if (hiddenServiceTasksError) {
          console.error('Failed to load hidden service tasks:', hiddenServiceTasksError);
        }

        // Create a map of closed suggested tasks: key = `${service_entry_id}:${task_text}`
        const closedSuggestedKeys = new Set<string>();
        if (hiddenServiceTasksData) {
          hiddenServiceTasksData.forEach((h: any) => {
            if (h.hidden || h.status === 'done' || h.status === 'completed') {
              const raw = String(h.task_text ?? '');
              const trimmed = raw.trim();
              closedSuggestedKeys.add(`${h.service_entry_id}:${raw}`);
              closedSuggestedKeys.add(`${h.service_entry_id}:${trimmed}`);
            }
          });
        }

        // Count tasks from suggested_next_fixes (excluding hidden/done)
        let serviceEntryTasksCount = 0;
        if (serviceEntriesData) {
          serviceEntriesData.forEach((entry: any) => {
            const suggestedFixes = entry.suggested_next_fixes || [];
            if (Array.isArray(suggestedFixes)) {
              suggestedFixes.forEach((rec: { fix?: string; due?: string } | string) => {
                const taskText = typeof rec === 'string' ? rec.trim() : (rec.fix?.trim() || '');
                if (!taskText) return;
                const hiddenKey = `${entry.id}:${taskText}`;
                if (!closedSuggestedKeys.has(hiddenKey)) {
                  serviceEntryTasksCount++;
                }
              });
            }
          });
        }

        // Count standalone tasks from tasks table
        const standaloneTasksCount = (standaloneTasksData || []).length;

        // Total count
        setTasksCount(serviceEntryTasksCount + standaloneTasksCount);
      } catch (error) {
        console.error('Error loading tasks count:', error);
        setTasksCount(0);
      }
    };

    loadTasksCount();
  }, [activeCar?.id, user?.id]);

  // Load active service requests for current car
  useEffect(() => {
    if (!activeCar?.id || !user?.id) {
      setActiveServiceRequests([]);
      return;
    }

    const loadServiceRequests = async () => {
      try {
        const { data, error } = await supabase
          .from('service_requests')
          .select('id, title, created_at, owner_notes')
          .eq('car_id', activeCar.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Failed to load service requests:', error);
          setActiveServiceRequests([]);
        } else {
          setActiveServiceRequests(data || []);
        }
      } catch (error) {
        console.error('Error loading service requests:', error);
        setActiveServiceRequests([]);
      }
    };

    loadServiceRequests();
  }, [activeCar?.id, user?.id]);

  // Load service entries count for active car
  useEffect(() => {
    if (!activeCar?.id) {
      setServiceEntriesCount(0);
      return;
    }

    const loadServiceEntriesCount = async () => {
      try {
        const { data, error } = await supabase
          .from('service_entries')
          .select('id')
          .eq('car_id', activeCar.id);

        if (error) {
          console.error('Failed to load service entries count:', error);
          setServiceEntriesCount(0);
          return;
        }

        setServiceEntriesCount((data || []).length);
      } catch (error) {
        console.error('Error loading service entries count:', error);
        setServiceEntriesCount(0);
      }
    };

    loadServiceEntriesCount();
  }, [activeCar?.id]);
  
  // Debug logging for car selection and issues
  useEffect(() => {
    if (activeCar) {
      console.log('🏎️ Active car:', {
        id: activeCar.id,
        nickname: activeCar.nickname,
        brand: activeCar.brand,
        model: activeCar.model,
        issuesCount: activeCar.issues?.length || 0,
        openIssues: activeCar.issues?.filter(issue => 
          issue.status === 'open' || issue.status === 'recurred'
        ) || []
      });
    }
  }, [activeCar]);

  // Debug: Log when cars or currentCar changes
  useEffect(() => {
    console.log('🚗 Cars or currentCar changed:', {
      carsLength: cars.length,
      currentCarId: currentCar?.id,
      activeCarId: activeCar?.id,
      carsIds: cars.map(c => c.id),
      carsWithIssues: cars.filter(c => c.issues && c.issues.length > 0).map(c => ({
        id: c.id,
        nickname: c.nickname,
        issuesCount: c.issues?.length || 0
      }))
    });
  }, [cars, currentCar, activeCar]);

  // Load profile photo on mount and when user ID changes
  useEffect(() => {
    const loadProfilePhoto = async () => {
      if (!user) {
        setProfilePhotoUrl(null);
        loadedUserIdRef.current = null;
        return;
      }

      // Skip if we've already loaded for this user ID (prevents re-querying on token refresh)
      if (loadedUserIdRef.current === user.id) {
        return;
      }

      try {
        // 1. First, check for custom uploaded photo in public.users.custom_avatar_url
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('custom_avatar_url')
          .eq('id', user.id)
          .single();

        if (!userError && userData?.custom_avatar_url) {
          // Custom uploaded photo exists - use it
          try {
            const signedUrlInfo = await getSignedViewUrl(userData.custom_avatar_url);
            setProfilePhotoUrl(signedUrlInfo.url);
            loadedUserIdRef.current = user.id; // Mark as loaded
            return; // Exit early - custom photo takes precedence
          } catch (error) {
            console.error('Failed to load custom profile photo:', error);
            // Fall through to Google avatar fallback
          }
        }

        // 2. Fallback to Google OAuth avatar_url from auth metadata
        const meta: any = user?.user_metadata || {};
        const googleAvatarUrl = meta.avatar_url;
        
        if (googleAvatarUrl) {
          // Google avatar is a direct URL, not a GCS path
          setProfilePhotoUrl(googleAvatarUrl);
        } else {
          // No avatar found
          setProfilePhotoUrl(null);
        }
        
        loadedUserIdRef.current = user.id; // Mark as loaded
      } catch (error) {
        console.error('Failed to load profile photo:', error);
        setProfilePhotoUrl(null);
        loadedUserIdRef.current = user.id; // Mark as loaded even on error to prevent retry loops
      }
    };

    loadProfilePhoto();
  }, [user?.id]);

  const displayName = useMemo(() => {
    const meta: any = user?.user_metadata || {};
    const full = meta.full_name || meta.name || meta.display_name || (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null);
    if (full && typeof full === 'string') return full;
    if (user?.email) {
      const handle = user.email.split('@')[0];
      return handle.charAt(0).toUpperCase() + handle.slice(1);
    }
    return 'User';
  }, [user?.user_metadata, user?.email]);

  return (
    <PageLayout hideNavigation={false}>
      <div className="space-y-3 pb-16 px-2 mt-3 sm:mt-4">
          {/* Header with greeting and profile button - always shown */}
          <div className="flex items-center justify-between gap-3">
            <h1 
              className="text-xl font-bold"
              style={{
                fontFamily: 'Lato',
                fontWeight: 700,
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '28px',
                letterSpacing: '0.025em',
                verticalAlign: 'middle'
              }}
            >
              {t('home.greeting', { name: displayName })}
            </h1>
            <button
              type="button"
              aria-label={t('home.myAccount')}
              onClick={() => navigate('/profile')}
              className="flex items-center justify-center rounded-full overflow-hidden"
              style={{ 
                width: 36, 
                height: 36, 
                background: profilePhotoUrl ? 'transparent' : 'rgba(112, 151, 250, 0.2)',
                border: profilePhotoUrl ? 'none' : 'none'
              }}
            >
              {profilePhotoUrl ? (
                <img
                  src={profilePhotoUrl}
                  alt={displayName || t('common.user')}
                  className="w-full h-full object-cover rounded-full"
                  onError={() => {
                    // Fallback to initial if image fails to load
                    setProfilePhotoUrl(null);
                  }}
                />
              ) : (
                <span style={{ color: '#7097fa', fontFamily: 'Lato', fontWeight: 700 }}>
                  {displayName?.charAt(0)?.toUpperCase()}
                </span>
              )}
            </button>
          </div>

      {/* Carousel Section - Always shown */}
      {isEmpty ? (
        <div 
          className="w-full overflow-hidden rounded-[10px] bg-[#FDFDFD]" 
          style={{ 
            height: '140px',
            maxWidth: '663px'
          }}
        >
          <Carousel
            opts={{ align: 'start', loop: false }}
            className="w-full h-full"
            setApi={setCarouselApi}
            showPeek={true}
          >
            <CarouselContent className="h-full">
              <CarouselItem className="h-[140px]">
                <div 
                  className="flex flex-col items-center justify-center bg-white rounded-[10px] overflow-hidden w-[98%] h-[95%] border-2 border-gray-200 shadow-md cursor-pointer mx-auto my-auto"
                  onClick={() => navigate('/cars/add')}
                >
                  <div className="flex-1 w-full flex items-center justify-center bg-gray-50">
                    <img
                      src="/icons/AddCarImage.png"
                      alt={t('common.addCar')}
                      className="w-[20%] h-auto object-contain"
                    />
                  </div>
                  <div className="w-full bg-gray-100 py-1.5 text-center">
                    <span 
                      className="text-xs font-semibold text-gray-800 tracking-wide"
                      style={{
                        fontFamily: 'Lato',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '16px',
                        letterSpacing: '0.3px'
                      }}
                    >
                      {t('common.addCar')}
                    </span>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
          </Carousel>
        </div>
      ) : (
        <>
          {/* Carousel Section */}
          <div 
            className="w-full overflow-hidden rounded-[10px] bg-[#FDFDFD]" 
            style={{ 
              height: '140px',
              maxWidth: '663px'
            }}
          >
            <Carousel
              opts={{ align: 'start', loop: false }}
              className="w-full h-full"
              setApi={setCarouselApi}
              showPeek={true}
            >
              <CarouselContent className="h-full">
                {carsForCarousel.length === 0 ? (
                  <CarouselItem className="h-[140px]">
                    <div className="flex flex-col items-center justify-center bg-white rounded-[10px] overflow-hidden w-[98%] h-[95%] border-2 border-gray-200 shadow-md cursor-pointer mx-auto my-auto">
                      <div className="flex-1 w-full flex items-center justify-center bg-gray-50">
                        <img
                          src="/icons/AddCarImage.png"
                          alt="Add car"
                          className="w-[20%] h-auto object-contain"
                        />
                      </div>
                      <div className="w-full bg-gray-100 py-1.5 text-center">
                        <span 
                          className="text-xs font-semibold text-gray-800 tracking-wide"
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 700,
                            fontSize: '12px',
                            lineHeight: '16px',
                            letterSpacing: '0.3px'
                          }}
                        >
                          Add car
                        </span>
                      </div>
                    </div>
                  </CarouselItem>
                ) : (
                  carsForCarousel.map(car => (
                    <CarouselItem key={car.id} className="h-[140px] mb-2">
                      <div 
                        className="bg-white rounded-[10px] overflow-hidden w-[98%] h-[95%] flex flex-col relative shadow-md cursor-pointer mr-2 mx-auto my-auto"
                        onClick={() => handleCarSelect(car.id)}
                      >
                        {/* Main car badge */}
                        {car.id === mainCarId && (
                          <div className="absolute top-1 left-1 z-20">
                            <Badge variant="default" className="text-xs px-1.5 py-0.5">{t('cars.main')}</Badge>
                          </div>
                        )}
                        {/* Actions menu in top right */}
                        <div className="absolute top-1 right-1 z-20">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="p-1 bg-white rounded-full shadow hover:bg-gray-100"
                                onClick={(e) => e.stopPropagation()}
                                aria-label={t('home.actions')}
                                type="button"
                              >
                                <MoreHorizontal size={18} className="text-gray-600" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[200px]">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/cars/${car.id}`);
                                }}
                              >
                                {t('home.openCarDetails')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/cars/edit/${car.id}`);
                                }}
                              >
                                {t('home.editCar')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/reports/health/${car.id}`);
                                }}
                              >
                                {t('home.healthReport')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div className="flex-1 w-full flex items-center justify-center bg-gray-50 relative overflow-hidden">
                          {/* Loading spinner while image is loading */}
                          {imageLoadingStates[car.id] && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                              <LoadingSpinner size="sm" />
                            </div>
                          )}
                          
                          {/* Car image - always render but hide until loaded */}
                          {signedUrls[car.id]?.url && (
                            <img
                              src={signedUrls[car.id].url}
                              alt={`${car.nickname || car.brand} ${car.model}`}
                              className={`absolute inset-0 w-full h-full object-cover ${
                                imageLoadingStates[car.id] ? 'opacity-0' : 'opacity-100'
                              }`}
                              referrerPolicy="no-referrer"
                              crossOrigin="anonymous"
                              onLoad={() => handleImageLoad(car.id)}
                              onError={() => handleImageError(car.id)}
                              onLoadStart={() => console.log(`🔄 Image load started for car ${car.id}`)}
                            />
                          )}
                          
                          {/* Fallback icon when no image or no signed URL */}
                          {!signedUrls[car.id]?.url && !imageLoadingStates[car.id] && (
                            <img
                              src="/icons/AddCarImage.png"
                              alt={t('common.addCarImage')}
                              className="w-[20%] h-auto object-contain"
                            />
                          )}
                          
                          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition z-5" />
                        </div>
                        <div className={`w-full py-1 text-center relative z-10 ${
                          selectedCarId === car.id ? 'bg-[#F1FE97] bg-opacity-90' : 'bg-[#F2F2F2] bg-opacity-90'
                        }`}>
                          <span 
                            className="text-xs font-bold text-black tracking-wide"
                            style={{
                              fontFamily: 'Lato',
                              fontWeight: 700,
                              fontSize: '12px',
                              lineHeight: '16px',
                              letterSpacing: '0.3px'
                            }}
                          >
                            {car.nickname || `${car.brand} ${car.model}`}
                          </span>
                        </div>
                      </div>
                    </CarouselItem>
                  ))
                )}
                {/* Add "Add new" card if there are cars */}
                {carsForCarousel.length > 0 && (
                  <CarouselItem className="h-[140px]">
                    <div 
                      className="flex flex-col items-center justify-center bg-white rounded-[10px] overflow-hidden w-[98%] h-[95%] border-2 border-gray-200 shadow-md cursor-pointer mx-auto my-auto"
                      onClick={() => navigate('/cars/add')}
                    >
                      <div className="flex-1 w-full flex items-center justify-center bg-gray-50">
                        <img
                          src="/icons/AddCarImage.png"
                          alt="Add car"
                          className="w-[20%] h-auto object-contain"
                        />
                      </div>
                      <div className="w-full bg-gray-100 py-1.5 text-center">
                        <span 
                          className="text-xs font-semibold text-gray-800 tracking-wide"
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 700,
                            fontSize: '12px',
                            lineHeight: '16px',
                            letterSpacing: '0.3px'
                          }}
                        >
                          {t('home.addNew')}
                        </span>
                      </div>
                    </div>
                  </CarouselItem>
                )}
          </CarouselContent>
          {carsForCarousel.length > 1 && (
            <div className="flex justify-center mt-2 gap-1">
              {carsForCarousel.map((_, index) => (
                <button 
                  key={index} 
                  className="focus:outline-none" 
                  onClick={() => {
                    if (carouselApi) {
                      carouselApi.scrollTo(index);
                    }
                  }}
                >
                  <div 
                    className={`w-6 h-1 rounded-full transition-all duration-300 ${
                      index === currentCarIndex 
                        ? 'bg-[#e7fe52]' 
                        : 'bg-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          )}
        </Carousel>
      </div>
          
          {/* CostsWidget - Full Width */}
          {activeCar && <CostsWidget carId={activeCar.id} />}

          {/* New activity card (between costs and “Report a problem”) */}
          {activeCar &&
            activityCounts &&
            (activityCounts.issuesNew > 0 ||
              activityCounts.tasksUpdated > 0 ||
              activityCounts.serviceRecordsUpdated > 0 ||
              activityCounts.expensesAdded > 0 ||
              activityCounts.carDetailsUpdated > 0) && (
              <div className="mt-3">
                <NewActivityCard
                  counts={activityCounts}
                  onClick={() => navigate(`/activity/${activeCar.id}`)}
                />
              </div>
            )}
        </>
      )}
      
      {/* ServiceCostsWidget placeholder when no cars */}
      {isEmpty && (
        <div className="w-full rounded-[20px] bg-[#FDFDFD] p-4" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}>
          <div className="text-center py-4">
            <p className="text-gray-500 text-sm">{t('home.addCarToGetStarted')}</p>
          </div>
        </div>
      )}
          
      {/* Content Section - Always shown */}
      <div className="space-y-3">
            <div className="mb-2">
              <Card 
                className="w-full rounded-[20px] bg-[#FDFDFD] flex flex-col gap-4"
                style={{
                  boxShadow: '0px 10px 8px -1px #EEEEEE'
                }}
              >
                <CardContent className="p-4 pb-4 flex flex-col gap-3">
                  <div className="flex flex-col gap-3">
                    <div>
                      <h3 
                        className="text-black"
                        style={{
                          fontFamily: 'Lato',
                          fontWeight: 700,
                          fontStyle: 'normal',
                          fontSize: '20px',
                          lineHeight: '28px',
                          letterSpacing: '0.025em'
                        }}
                      >
                        {t('home.havingTrouble')}
                      </h3>
                      <p 
                        className="text-gray-600 mt-1"
                        style={{
                          fontFamily: 'Lato',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '12px',
                          lineHeight: '16px',
                          letterSpacing: '0.1px'
                        }}
                      >
                        {t('home.troubleDescription')}
                      </p>
                    </div>
                    <div className="flex flex-col [@media(min-width:321px)]:flex-row justify-between items-stretch [@media(min-width:321px)]:items-center w-full gap-1 min-w-0">
                      <Button 
                        variant="secondary"
                        className="text-xs px-1 sm:px-2 py-1 h-8 flex-1 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis"
                        onClick={() => setAIAssistantModalOpen(true)}
                      >
                        {t('home.chat')}
                      </Button>
                      <Button 
                        className="bg-lime text-black hover:bg-lime/90 text-xs px-1 sm:px-2 py-1 h-8 flex-1 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis"
                        onClick={() => navigate('/log-symptom')}
                      >
                        {t('home.addIssue')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Three additional cards */}
            <div className="space-y-2">
              <Card 
                className="w-full h-[64px] rounded-[20px] bg-[#FDFDFD]"
                style={{
                  boxShadow: '0px 10px 8px -1px #EEEEEE'
                }}
              >
                <CardContent className="p-3 h-full flex items-center">
                  <div className="w-full flex items-center justify-between" onClick={() => activeCar ? navigate(`/records/${activeCar.id}`) : navigate('/cars/add')} style={{ cursor: activeCar ? 'pointer' : 'default' }}>
                    <div className="flex items-center">
                      <img 
                        src="/icons/Error.png" 
                        alt={t('common.errorIcon')} 
                        className="w-10 h-10 mr-3"
                      />
                      <div>
                        <h4 
                          className="text-[#070707]"
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 700,
                            fontStyle: 'normal',
                            fontSize: '14px',
                            lineHeight: '20px',
                            letterSpacing: '0.3px'
                          }}
                        >
                          {t('home.issuesRecorded')}
                        </h4>
                        <p 
                          className="text-gray-600 mt-1"
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '16px'
                          }}
                        >
                          {(() => {
                            if (!activeCar) {
                              return t('home.addCarToGetStarted');
                            }
                            
                            // Use the active (selected) car directly
                            if (!activeCar?.issues) {
                              return t('home.noOpenIssues');
                            }
                            
                            // Separate draft issues from active open issues
                            const draftIssues = activeCar.issues.filter(issue => 
                              issue.status === 'draft'
                            );
                            
                            const activeOpenIssues = activeCar.issues.filter(issue => 
                              issue.status === 'open' || issue.status === 'recurred'
                            );
                            
                            const totalActiveIssues = activeOpenIssues.length;
                            const totalDraftIssues = draftIssues.length;
                            
                            // Show active open issues count, with draft info if applicable
                            if (totalActiveIssues > 0) {
                              if (totalDraftIssues > 0) {
                                return `${totalActiveIssues} ${totalActiveIssues === 1 ? t('home.openIssue') : t('home.openIssues')} (${totalDraftIssues} ${totalDraftIssues === 1 ? t('home.draftIssue') : t('home.draftIssues')})`;
                              } else {
                                return `${totalActiveIssues} ${totalActiveIssues === 1 ? t('home.openIssue') : t('home.openIssues')}`;
                              }
                            } else if (totalDraftIssues > 0) {
                              return `${totalDraftIssues} ${totalDraftIssues === 1 ? t('home.draftIssue') : t('home.draftIssues')}`;
                            } else {
                              return t('home.noOpenIssues');
                            }
                          })()}
                        </p>
                      </div>
                    </div>
                    <img 
                      src="/icons/Component 3.png" 
                      alt={t('common.component3')} 
                      className="w-5 h-5"
                    />
                  </div>
                </CardContent>
              </Card>
              
              {/* Copy 1 - Notification Card */}
              <Card 
                className="w-full h-[64px] rounded-[20px] bg-[#FDFDFD]"
                style={{
                  boxShadow: '0px 10px 8px -1px #EEEEEE'
                }}
              >
                <CardContent className="p-3 h-full flex items-center">
                  <div className="w-full flex items-center justify-between" onClick={() => activeCar ? navigate(`/records/${activeCar.id}?tab=tasks`) : navigate('/cars/add')} style={{ cursor: activeCar ? 'pointer' : 'default' }}>
                    <div className="flex items-center">
                      <img 
                        src="/icons/Notification.png" 
                        alt={t('common.notificationIcon')} 
                        className="w-10 h-10 mr-3"
                      />
                      <div>
                        <h4 
                          className="text-[#070707]"
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 700,
                            fontStyle: 'normal',
                            fontSize: '14px',
                            lineHeight: '20px',
                            letterSpacing: '0.3px'
                          }}
                        >
                          {t('home.scheduledTasks')}
                        </h4>
                        <p 
                          className="text-gray-600 mt-1"
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '16px'
                          }}
                        >
                          {tasksCount > 0 
                            ? `${tasksCount} ${tasksCount === 1 ? t('home.task') : t('home.scheduledTasks')}`
                            : t('home.noScheduledTasks')
                          }
                        </p>
                      </div>
                    </div>
                    <img 
                      src="/icons/Component 3.png" 
                      alt={t('common.component3')} 
                      className="w-5 h-5"
                    />
                  </div>
                </CardContent>
              </Card>
              
              {/* Copy 2 - Third Card */}
              <Card 
                className="w-full h-[64px] rounded-[20px] bg-[#FDFDFD]"
                style={{
                  boxShadow: '0px 10px 8px -1px #EEEEEE'
                }}
              >
                <CardContent className="p-3 h-full flex items-center">
                  <div className="w-full flex items-center justify-between" onClick={() => activeCar ? navigate(`/records/${activeCar.id}?tab=repairs`) : navigate('/cars/add')} style={{ cursor: activeCar ? 'pointer' : 'default' }}>
                    <div className="flex items-center">
                      <img 
                        src="/icons/Good.png" 
                        alt={t('common.goodIcon')} 
                        className="w-10 h-10 mr-3"
                      />
                      <div>
                        <h4 
                          className="text-[#070707]"
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 700,
                            fontStyle: 'normal',
                            fontSize: '14px',
                            lineHeight: '20px',
                            letterSpacing: '0.3px'
                          }}
                        >
                          {t('home.serviceHistory')}
                        </h4>
                        <p 
                          className="text-gray-600 mt-1"
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '16px'
                          }}
                        >
                          {serviceEntriesCount > 0 
                            ? `${serviceEntriesCount} ${serviceEntriesCount === 1 ? t('home.record') : t('home.serviceRecords')}`
                            : t('home.noRecords')
                          }
                        </p>
                      </div>
                    </div>
                    <img 
                      src="/icons/Component 3.png" 
                      alt={t('common.component3')} 
                      className="w-5 h-5"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Active Service Requests Section */}
            {activeServiceRequests.length > 0 && (
              <div className="w-full mt-6 space-y-3">
                <h3 
                  className="text-lg font-semibold"
                  style={{
                    fontFamily: 'Lato',
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: '22px'
                  }}
                >
                  {t('serviceRequest.activeRequests') || 'Active Service Requests'}
                </h3>
                {activeServiceRequests.map((request) => (
                  <Card 
                    key={request.id}
                    className="w-full rounded-[20px] bg-[#FDFDFD]"
                    style={{
                      boxShadow: '0px 10px 8px -1px #EEEEEE'
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {request.title || t('serviceRequest.title') || 'Service Request'}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {t('serviceRequest.createdAt') || t('common.created')}: {new Date(request.created_at).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                            {request.owner_notes.substring(0, 100)}
                            {request.owner_notes.length > 100 ? '...' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/service-request/view/${request.id}`)}
                        >
                          {t('serviceRequest.view') || 'View'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            const url = `${window.location.origin}/service-request/view/${request.id}`;
                            navigator.clipboard.writeText(url).then(() => {
                              toast({
                                title: t('serviceRequest.linkCopied') || 'Link copied',
                                description: t('serviceRequest.linkCopiedDesc') || 'Service request link copied to clipboard',
                              });
                            });
                          }}
                        >
                          {t('serviceRequest.shareAgain') || 'Share again'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={async () => {
                            try {
                              const { error } = await supabase
                                .from('service_requests')
                                .update({ 
                                  status: 'closed',
                                  closed_at: new Date().toISOString()
                                })
                                .eq('id', request.id);

                              if (error) throw error;
                              setActiveServiceRequests(prev => prev.filter(r => r.id !== request.id));
                              toast({
                                title: t('serviceRequest.closed') || 'Closed',
                                description: t('serviceRequest.closedDesc') || 'Service request closed',
                              });
                            } catch (error) {
                              console.error('Error closing service request:', error);
                              toast({
                                title: t('serviceRequest.error') || 'Error',
                                description: t('serviceRequest.closeError') || 'Failed to close service request',
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          {t('serviceRequest.close') || 'Close'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={async () => {
                            try {
                              const { error } = await supabase
                                .from('service_requests')
                                .update({ 
                                  status: 'cancelled',
                                  cancelled_at: new Date().toISOString()
                                })
                                .eq('id', request.id);

                              if (error) throw error;
                              setActiveServiceRequests(prev => prev.filter(r => r.id !== request.id));
                              toast({
                                title: t('serviceRequest.cancelled') || 'Cancelled',
                                description: t('serviceRequest.cancelledDesc') || 'Service request cancelled',
                              });
                            } catch (error) {
                              console.error('Error cancelling service request:', error);
                              toast({
                                title: t('serviceRequest.error') || 'Error',
                                description: t('serviceRequest.cancelError') || 'Failed to cancel service request',
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          {t('serviceRequest.cancel') || 'Cancel'}
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-lime text-black hover:bg-[#d4e848]"
                          onClick={() => navigate(`/services/add?requestId=${request.id}`)}
                        >
                          {t('serviceRequest.addServiceEntry') || 'Add a Service Entry'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
        </div>
      </div>
      
      <AIAssistantComingSoonModal
        open={aiAssistantModalOpen}
        onOpenChange={setAIAssistantModalOpen}
      />
    </PageLayout>
  );
};

export default HomePage;